import { EditOutlined, FileTextOutlined, SendOutlined, TagsOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { BlogPostStatus, listBlogPosts } from '../../api/blog';
import type { BlogPostFilter } from '../../api/blog';
import { StatTiles } from '../../layout/StatTiles';
import type { StatTile } from '../../layout/StatTiles';
import { blogStatusColors } from './blogDisplay';
import { BrandColors } from '../../theme/brand';

interface PostCounts {
  readonly total: number;
  readonly published: number;
  readonly drafts: number;
}

/** Đếm số bài khớp một điều kiện: chỉ xin một dòng, lấy con số tổng máy chủ trả kèm. */
async function countPosts(filter: BlogPostFilter): Promise<number> {
  const page = await listBlogPosts({ ...filter, pageNumber: 1, pageSize: 1 });

  return page.totalCount;
}

/**
 * Dải số liệu đầu trang Blog. Không chịu ảnh hưởng của bộ lọc bên dưới.
 *
 * "Đã đăng" gồm cả bài hẹn giờ — chúng đã được duyệt đăng, chỉ chờ tới ngày.
 */
export function BlogSummary({
  reloadToken,
  categoryCount,
}: {
  readonly reloadToken: number;
  readonly categoryCount: number | undefined;
}): ReactNode {
  const [counts, setCounts] = useState<PostCounts | null>(null);

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      try {
        const [total, published, drafts] = await Promise.all([
          countPosts({}),
          countPosts({ status: BlogPostStatus.Published }),
          countPosts({ status: BlogPostStatus.Draft }),
        ]);

        if (isCurrent) {
          setCounts({ total, published, drafts });
        }
      } catch {
        // Dải số liệu chỉ để tham khảo; danh sách bên dưới đã tự báo lỗi tải.
        if (isCurrent) {
          setCounts(null);
        }
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [reloadToken]);

  const items: StatTile[] = [
    { key: 'total', label: 'Tổng bài viết', icon: <FileTextOutlined />, color: BrandColors.ink, value: counts?.total },
    {
      key: 'published',
      label: 'Đã đăng',
      icon: <SendOutlined />,
      color: blogStatusColors.published,
      value: counts?.published,
    },
    { key: 'drafts', label: 'Bản nháp', icon: <EditOutlined />, color: blogStatusColors.scheduled, value: counts?.drafts },
    { key: 'categories', label: 'Danh mục', icon: <TagsOutlined />, color: BrandColors.blue, value: categoryCount },
  ];

  return <StatTiles items={items} />;
}
